import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { playSound } from '../lib/soundEffects';

const { FiEdit3, FiX, FiCheck, FiCopy, FiTrash2 } = FiIcons;

export default function FolderScratchpadModal({
  isOpen,
  onClose,
  folderId = 'root',
  folderTitle = 'Home',
}) {
  const storageKey = `memark_scratchpad_${folderId}`;
  const [content, setContent] = useState('');
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const stored = localStorage.getItem(storageKey) || '';
      setContent(stored);
      setSaved(false);
    }
  }, [isOpen, storageKey]);

  const handleChange = (e) => {
    const val = e.target.value;
    setContent(val);
    localStorage.setItem(storageKey, val);
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };

  const handleCopy = () => {
    playSound('copy');
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleClear = () => {
    playSound('delete');
    setContent('');
    localStorage.removeItem(storageKey);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[85] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/75 backdrop-blur-md"
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.94, y: 15 }}
          className="relative w-full max-w-lg rounded-2xl bg-[#131317] border border-[#272732] shadow-2xl p-6 z-10 flex flex-col max-h-[85vh]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between pb-3 border-b border-[#23232c] mb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
                <SafeIcon icon={FiEdit3} className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-white flex items-center gap-2">
                  <span>Folder Scratchpad</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-[#888]">
                    {folderTitle}
                  </span>
                </h3>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {saved && (
                <span className="text-[11px] text-emerald-400 flex items-center gap-1">
                  <SafeIcon icon={FiCheck} className="w-3 h-3" />
                  <span>Saved</span>
                </span>
              )}
              <button
                onClick={onClose}
                className="p-1 rounded-lg text-[#666] hover:text-white hover:bg-[#222] transition-colors"
              >
                <SafeIcon icon={FiX} className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Text Area */}
          <textarea
            value={content}
            onChange={handleChange}
            placeholder={`Jot down quick thoughts, markdown snippets, or project notes for "${folderTitle}"...`}
            rows={12}
            className="w-full flex-1 p-3.5 rounded-xl bg-[#181820] border border-[#262632] text-sm text-white font-mono focus:outline-none focus:border-emerald-400/60 resize-none leading-relaxed transition-colors placeholder:text-[#555]"
            autoFocus
          />

          {/* Footer */}
          <div className="flex items-center justify-between mt-3 pt-2 border-t border-[#23232c] text-xs">
            <span className="text-[#666]">
              {content.length} characters • auto-saved locally
            </span>

            <div className="flex items-center gap-2">
              {content && (
                <>
                  <button
                    onClick={handleClear}
                    className="p-1.5 rounded-lg text-red-400/70 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                    title="Clear scratchpad"
                  >
                    <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={handleCopy}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#22222a] hover:bg-[#2c2c36] text-[#aaa] hover:text-white transition-colors"
                  >
                    <SafeIcon icon={copied ? FiCheck : FiCopy} className="w-3 h-3 text-emerald-400" />
                    <span>{copied ? 'Copied' : 'Copy'}</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
